import React from "react";
import { Link } from "react-router-dom";
import { Printer, ArrowLeft, Phone, Sparkles } from "lucide-react";
import Seo from "@/components/Seo";
import { CLINIC } from "@/lib/clinicInfo";

export default function OculoplastyGuide() {
  const handlePrint = () => window.print();
  return (
    <>
      <Seo
        title="Your Eyelid & Oculoplasty Guide — Nehchal Eye Centre Noida"
        description="A private counselling guide from Dr. Gurkiran Kaur — understand your functional and aesthetic eyelid options and decide what's right for you."
        path="/eyelid-oculoplasty/guide"
      />

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .grain::before { display: none !important; }
          .oc-page { box-shadow: none !important; margin: 0 !important; border: none !important; page-break-after: always; }
          .oc-page:last-child { page-break-after: auto; }
        }
        @page { size: A4; margin: 0; }
        .oc-page {
          width: 210mm;
          min-height: 297mm;
          padding: 22mm 20mm;
          background: #fbf9f4;
          margin: 0 auto 24px;
          box-shadow: 0 8px 30px rgba(0,0,0,0.08);
          position: relative;
          break-inside: avoid;
        }
        .oc-brand-strip {
          position: absolute; top: 0; left: 0; right: 0; height: 6px;
          background: linear-gradient(90deg, #0f5c5d 0%, #c9964c 100%);
        }
        .oc-footer {
          position: absolute; bottom: 12mm; left: 20mm; right: 20mm;
          display: flex; justify-content: space-between; align-items: center;
          font-size: 10px; color: #6b7280;
          border-top: 1px solid #e5e0d5; padding-top: 8px;
        }
        .oc-eye {
          width: 100%; max-width: 320px;
        }
      `}</style>

      <div className="no-print bg-secondary/50 border-b border-border">
        <div className="container-wide py-4 flex flex-wrap items-center justify-between gap-3">
          <Link to="/eyelid-oculoplasty" className="inline-flex items-center gap-2 text-sm text-primary hover:opacity-80">
            <ArrowLeft className="h-4 w-4" /> Back to Eyelid &amp; Oculoplasty
          </Link>
          <div className="flex items-center gap-3">
            <a href={`tel:${CLINIC.phoneRaw}`} className="hidden sm:inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm hover:bg-secondary">
              <Phone className="h-4 w-4" /> {CLINIC.phone}
            </a>
            <button onClick={handlePrint} className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm hover:bg-primary/90" data-testid="oc-print-btn">
              <Printer className="h-4 w-4" /> Download / Print PDF
            </button>
          </div>
        </div>
      </div>

      <div className="py-10 bg-secondary/30 print:bg-white">

        {/* COVER */}
        <article className="oc-page">
          <div className="oc-brand-strip" />
          <div className="pt-12">
            <img src={CLINIC.logo} alt="Nehchal Eye Centre" className="h-14 w-auto object-contain" />
            <div className="mt-16">
              <div className="text-[10px] tracking-[0.35em] uppercase text-[#c9964c]">Patient Counselling Guide</div>
              <h1 className="font-serif text-5xl leading-[1.05] text-[#1a2332] mt-4">
                Your Eyelid<br />&amp; Oculoplasty Guide.
              </h1>
              <p className="mt-6 text-[15px] leading-7 text-[#1a2332]/80 max-w-md">
                A private guide to understanding your functional and aesthetic eyelid options — prepared for you by <strong>Dr. Gurkiran Kaur</strong>, fellowship-trained oculoplastic surgeon.
              </p>
              <div className="mt-16 inline-flex items-center gap-2 rounded-full bg-[#0f5c5d]/10 text-[#0f5c5d] px-3.5 py-1.5 text-[10px] tracking-[0.2em] uppercase">
                <Sparkles className="h-3 w-3" /> Please bring this to your consultation
              </div>
            </div>
          </div>
          <div className="oc-footer"><span>Nehchal Eye Centre · Sector 32, Noida</span><span>nehchaleye.com</span></div>
        </article>

        {/* PAGE 1 — Understanding Oculoplasty */}
        <article className="oc-page">
          <div className="oc-brand-strip" />
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#c9964c]">Page 01</div>
          <h2 className="font-serif text-4xl leading-tight text-[#1a2332] mt-3">
            Understanding <em className="text-[#0f5c5d]">oculoplasty.</em>
          </h2>
          <div className="mt-6 space-y-5 text-[15px] leading-7 text-[#1a2332]/85 max-w-lg">
            <p className="font-serif text-xl text-[#0f5c5d] italic">
              Your eyelids sit millimetres from your eye. Precision matters.
            </p>
            <p>Oculoplasty is the eye-focused subspecialty of the eyelids, tear ducts, orbit and the delicate area around the eyes. Every procedure — functional or cosmetic — protects vision first.</p>
          </div>

          <div className="mt-8 grid grid-cols-2 gap-4 max-w-2xl">
            <div className="rounded-lg border border-[#e5e0d5] bg-white p-5">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#0f5c5d]">Functional</div>
              <div className="font-serif text-lg text-[#1a2332] mt-1">Medical eyelid surgery</div>
              <ul className="mt-3 text-[13px] leading-6 text-[#1a2332]/80 space-y-1">
                <li>Ptosis (droopy eyelid)</li>
                <li>Entropion &amp; Ectropion</li>
                <li>Eyelid lesions &amp; cysts</li>
                <li>Eyelid reconstruction</li>
                <li>Tear duct surgery (DCR)</li>
              </ul>
              <div className="mt-4 text-[10px] text-[#0f5c5d]/70">Usually covered by health insurance</div>
            </div>
            <div className="rounded-lg border border-[#e5e0d5] bg-white p-5">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#c9964c]">Aesthetic</div>
              <div className="font-serif text-lg text-[#1a2332] mt-1">Periocular aesthetics</div>
              <ul className="mt-3 text-[13px] leading-6 text-[#1a2332]/80 space-y-1">
                <li>Upper &amp; lower blepharoplasty</li>
                <li>Under-eye bag correction</li>
                <li>Periocular ageing</li>
                <li>Botulinum toxin injections &amp; hyaluronic-acid fillers</li>
                <li>Eyelid asymmetry</li>
              </ul>
              <div className="mt-4 text-[10px] text-[#c9964c]">Self-pay · private care</div>
            </div>
          </div>
          <div className="oc-footer"><span>Your Eyelid &amp; Oculoplasty Guide</span><span>01</span></div>
        </article>

        {/* PAGE 2 — Options at a glance */}
        <article className="oc-page">
          <div className="oc-brand-strip" />
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#c9964c]">Page 02</div>
          <h2 className="font-serif text-4xl leading-tight text-[#1a2332] mt-3">
            Options at a glance.
          </h2>
          <div className="mt-8 rounded-lg overflow-hidden border border-[#e5e0d5]">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-[#0f5c5d] text-white">
                  <th className="text-left px-4 py-3 font-serif font-medium">Procedure</th>
                  <th className="text-left px-4 py-3 font-serif font-medium">Best Suited For</th>
                  <th className="text-left px-4 py-3 font-serif font-medium">Downtime</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {[
                  { p: "Upper Blepharoplasty", b: "Heavy or hooded upper lids", d: "7–10 days" },
                  { p: "Lower Blepharoplasty (Scarless)", b: "Under-eye bags without visible scar", d: "5–7 days" },
                  { p: "Ptosis Correction", b: "Droopy lid affecting vision or appearance", d: "10–14 days" },
                  { p: "DCR (Tear Duct)", b: "Chronic watering eyes", d: "7–10 days" },
                  { p: "Tear-Trough Fillers", b: "Under-eye hollows and shadows", d: "24–48 hours" },
                  { p: "Botulinum toxin injection (Crow's Feet / Frown)", b: "Dynamic wrinkles", d: "No downtime" },
                ].map((row, i) => (
                  <tr key={row.p} className={i % 2 ? "bg-[#faf8f3]" : ""}>
                    <td className="px-4 py-3 font-serif text-[#0f5c5d] font-medium">{row.p}</td>
                    <td className="px-4 py-3 text-[#1a2332]/85">{row.b}</td>
                    <td className="px-4 py-3 text-[#1a2332]/85">{row.d}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-8 rounded-lg border-l-4 border-[#c9964c] bg-white p-6">
            <div className="font-serif text-2xl italic text-[#0f5c5d]">
              &ldquo;The best procedure is the one that fits your anatomy.&rdquo;
            </div>
            <p className="mt-3 text-[14px] leading-7 text-[#1a2332]/80">
              Hollowing responds to filler. Dynamic wrinkles respond to botulinum toxin. True skin or fat excess needs surgery. Choosing correctly is the entire consultation.
            </p>
          </div>
          <div className="oc-footer"><span>Your Eyelid &amp; Oculoplasty Guide</span><span>02</span></div>
        </article>

        {/* PAGE 3 — Tell us about your goals */}
        <article className="oc-page">
          <div className="oc-brand-strip" />
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#c9964c]">Page 03</div>
          <h2 className="font-serif text-4xl leading-tight text-[#1a2332] mt-3">
            Tell us about your goals.
          </h2>
          <p className="mt-4 text-[14px] text-[#1a2332]/75 max-w-lg">
            Please tick the boxes that apply to you. Your answers help us decide between surgical, injectable, or combined care.
          </p>

          <div className="mt-8 space-y-3 max-w-xl">
            {[
              "Do people often ask if you look tired even when rested?",
              "Do your upper eyelids feel heavy or block your side vision?",
              "Do you notice hollowing or dark shadows under your eyes?",
              "Do you have puffy under-eye bags that don't settle with sleep?",
              "Are you bothered by fine lines around the eyes when you smile?",
              "Do you have watering or discharge from the eyes chronically?",
              "Is your left eyelid noticeably different from your right?",
              "Are you looking for a natural refreshed result, not a dramatic change?",
              "Do you prefer non-surgical options first if they can achieve most of the result?",
            ].map((q, i) => (
              <div key={q} className="flex items-start gap-4 rounded-lg border border-[#e5e0d5] bg-white p-4">
                <div className="text-[#c9964c] font-serif text-lg font-medium min-w-[24px]">{String(i + 1).padStart(2, "0")}</div>
                <div className="flex-1 text-[14px] text-[#1a2332] leading-6">{q}</div>
                <div className="flex gap-2 items-center text-[11px] text-[#1a2332]/70">
                  <span className="inline-flex items-center gap-1"><span className="inline-block h-4 w-4 border-[1.5px] border-[#0f5c5d] rounded-sm" /> Yes</span>
                  <span className="inline-flex items-center gap-1"><span className="inline-block h-4 w-4 border-[1.5px] border-[#0f5c5d] rounded-sm" /> No</span>
                </div>
              </div>
            ))}
          </div>
          <div className="oc-footer"><span>Your Eyelid &amp; Oculoplasty Guide</span><span>03</span></div>
        </article>

        {/* PAGE 4 — Recovery expectations + anatomy */}
        <article className="oc-page">
          <div className="oc-brand-strip" />
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#c9964c]">Page 04</div>
          <h2 className="font-serif text-4xl leading-tight text-[#1a2332] mt-3">
            The delicate anatomy we protect.
          </h2>

          <div className="mt-6 grid grid-cols-2 gap-8 items-start">
            {/* Simple SVG anatomy diagram */}
            <div>
              <svg viewBox="0 0 320 220" className="oc-eye" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="ir" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#0f5c5d" />
                    <stop offset="100%" stopColor="#083b3c" />
                  </linearGradient>
                </defs>
                <path d="M20 110 Q 160 20 300 110 Q 160 200 20 110 Z" fill="#faf8f3" stroke="#1a2332" strokeWidth="1.5" />
                <circle cx="160" cy="110" r="55" fill="url(#ir)" />
                <circle cx="160" cy="110" r="18" fill="#1a2332" />
                <circle cx="150" cy="100" r="6" fill="#faf8f3" opacity="0.8" />
                <path d="M20 110 Q 160 20 300 110" fill="none" stroke="#c9964c" strokeWidth="0.8" strokeDasharray="4 3" />
                <text x="20" y="45" fontSize="10" fill="#0f5c5d">Upper lid crease</text>
                <line x1="80" y1="50" x2="120" y2="70" stroke="#0f5c5d" strokeWidth="0.8" />
                <text x="200" y="200" fontSize="10" fill="#c9964c">Tear trough</text>
                <line x1="200" y1="195" x2="180" y2="175" stroke="#c9964c" strokeWidth="0.8" />
                <text x="20" y="200" fontSize="10" fill="#0f5c5d">Lower lid margin</text>
                <line x1="80" y1="195" x2="120" y2="175" stroke="#0f5c5d" strokeWidth="0.8" />
              </svg>
              <p className="text-[10px] text-[#1a2332]/60 mt-2 italic">Simplified periocular anatomy — surgical planning is always personalised.</p>
            </div>

            <div className="space-y-3">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#c9964c]">Recovery timeline</div>
              {[
                { d: "Day 1–3", n: "Cold compresses, no strenuous activity, expect mild bruising and swelling." },
                { d: "Day 7–10", n: "Sutures removed (if used). Most patients return to office work with sunglasses." },
                { d: "Week 2–4", n: "Visible swelling settles. Camouflage make-up allowed." },
                { d: "Week 4–6", n: "Final surgical result matures. Photographs at follow-up." },
              ].map((r) => (
                <div key={r.d} className="rounded-lg border border-[#e5e0d5] bg-white p-3">
                  <div className="font-serif text-[15px] text-[#0f5c5d]">{r.d}</div>
                  <p className="text-[12px] leading-5 text-[#1a2332]/80 mt-1">{r.n}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="oc-footer"><span>Your Eyelid &amp; Oculoplasty Guide</span><span>04</span></div>
        </article>

        {/* PAGE 5 — Consultation checklist */}
        <article className="oc-page">
          <div className="oc-brand-strip" />
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#c9964c]">Page 05</div>
          <h2 className="font-serif text-4xl leading-tight text-[#1a2332] mt-3">
            Your consultation checklist.
          </h2>
          <p className="mt-4 text-[14px] text-[#1a2332]/75 max-w-lg">
            Please bring these to your oculoplasty consultation so we can plan the right procedure for you.
          </p>
          <ul className="mt-8 space-y-3 max-w-lg">
            {[
              "This filled-in guide",
              "Photos of yourself from 5–10 years ago (helps set realistic expectations)",
              "Complete list of medications (especially blood thinners, aspirin, supplements)",
              "Any previous eyelid or facial surgery records",
              "Your health-insurance card if the procedure is functional",
              "A friend or family member for the ride home if we plan any minor procedure on the day",
              "A written list of things that bother you when you look in the mirror",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3 text-[14px] leading-6 text-[#1a2332]">
                <span className="inline-block h-4 w-4 border-[1.5px] border-[#0f5c5d] rounded-sm mt-1 shrink-0" />
                {item}
              </li>
            ))}
          </ul>

          <div className="mt-12 grid grid-cols-2 gap-6 max-w-xl">
            <div className="rounded-lg border border-[#e5e0d5] bg-white p-5">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#c9964c]">Book your consultation</div>
              <div className="font-serif text-lg text-[#0f5c5d] mt-2">{CLINIC.phone}</div>
              <div className="text-[12px] text-[#1a2332]/70 mt-1">Mon–Sat · 9 AM – 7 PM</div>
              <div className="text-[12px] text-[#1a2332]/70">Sun · 10 AM – 1 PM</div>
            </div>
            <div className="rounded-lg border border-[#e5e0d5] bg-white p-5">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#c9964c]">Visit</div>
              <div className="font-serif text-sm text-[#0f5c5d] mt-2 leading-5">
                {CLINIC.address.line1}<br />
                {CLINIC.address.line2}
              </div>
              <div className="text-[12px] text-[#1a2332]/70 mt-2">nehchaleye.com</div>
            </div>
          </div>

          <div className="mt-10 pt-5 border-t border-[#e5e0d5] max-w-lg">
            <p className="text-[11px] text-[#1a2332]/60 leading-5 italic">
              This guide is a counselling tool prepared by Dr. Gurkiran Kaur and does not replace a personal medical consultation. Every recommendation is finalised only after a comprehensive periocular examination.
            </p>
          </div>

          <div className="oc-footer"><span>Your Eyelid &amp; Oculoplasty Guide</span><span>05</span></div>
        </article>
      </div>
    </>
  );
}
